/**
 * Pixi Application Wrapper
 * 
 * Phase 6.0 Track C: Core Pixi.js application management
 */

import { Application } from "pixi.js";

export interface PixiAppOptions {
  width: number;
  height: number;
  backgroundColor?: number;
  backgroundAlpha?: number;
  antialias?: boolean;
  resolution?: number;
}

/**
 * Pixi Application Manager
 * 
 * Handles Pixi app lifecycle, canvas management, and resize
 */
export class PixiApp {
  private app: Application | null = null;
  private container: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;

  /**
   * Initialize Pixi application
   */
  async init(container: HTMLElement, options: PixiAppOptions): Promise<void> {
    this.container = container;

    // Create Pixi app
    this.app = new Application();

    // CRITICAL: Explicit resolution + transparent background
    const explicitResolution = options.resolution || 3;
    
    await this.app.init({
      width: options.width,
      height: options.height,
      backgroundColor: options.backgroundColor || 0xffffff,
      backgroundAlpha: options.backgroundAlpha !== undefined ? options.backgroundAlpha : 0, // DEFAULT TRANSPARENT
      antialias: true,
      resolution: explicitResolution, // Explicit resolution (not devicePixelRatio)
      autoDensity: true, // ENABLED - sets canvas.style.width/height to logical size
      preference: 'webgl',
      powerPreference: 'high-performance',
    });
    
    console.log('[PixiApp] Initialized with:', {
      logicalSize: `${options.width}×${options.height}`,
      resolution: explicitResolution,
      actualCanvas: `${this.app.canvas.width}×${this.app.canvas.height}`,
      canvasStyle: `${this.app.canvas.style.width}×${this.app.canvas.style.height}`,
      devicePixelRatio: window.devicePixelRatio,
    });

    // Append canvas to container
    container.appendChild(this.app.canvas);

    // Setup resize handling
    this.setupResize();
  }

  /**
   * Setup responsive resize
   */
  private setupResize(): void {
    if (!this.container || !this.app) return;

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        this.resize(width, height);
      }
    });

    this.resizeObserver.observe(this.container);
  }

  /**
   * Resize canvas
   */
  resize(width: number, height: number): void {
    if (!this.app) return;

    this.app.renderer.resize(width, height);
  }

  /**
   * Get Pixi app instance
   */
  getApp(): Application | null {
    return this.app;
  }

  /**
   * Get stage
   */
  getStage() {
    return this.app?.stage;
  }

  /**
   * Destroy app and cleanup
   * 
   * IMPORTANT: Does NOT destroy textures (managed by PIXI.Assets)
   * Caller should unload textures via PIXI.Assets.unload() before calling destroy()
   */
  destroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.app) {
      // Don't destroy textures - they're managed by PIXI.Assets
      this.app.destroy(true, { children: true, texture: false });
      this.app = null;
    }

    this.container = null;
  }
}
